import { thunk, action } from "easy-peasy";
import type { Action, Thunk } from "easy-peasy";
import axios from "axios";
import * as Sentry from "@sentry/react";

import { getLocalMediaRecordingInstance } from "../local_recording/utils";
import { watchLocalRecordingCallState } from "../local_recording/sagas/watchLocalRecordingCallState";
import { reportLocalRecordingCallState } from "../local_recording/sagas/reportLocalRecordingCallState";
import type { InMemoryLocalAudioStream } from "../../../lib/localRecording";
import type { InMemoryLocalMediaStream } from "../../../lib/localRecordingNew";
import type { FeatureFlagState } from "../feature_flagging/reducer";
import { getWindowDimensions } from "../../../components/Interview_dolby/common/getWindowDimensions";

import type {
  AuthData,
  ChatMessage,
  Devices,
  DolbyCall,
  JoinConference,
  JoinRoomApiResponse,
  LocalRecordingInfo,
  RefreshToken,
  StartRoomApiResponse,
  UpdateConference,
} from "./interfaces";
import { VideoCallProvider, CallStatus } from "./enums";

interface DolbyCallState {
  call: DolbyCall;
}

const newState = {
  status: CallStatus.PENDING,
  screenSize: getWindowDimensions(),
  token: "",
  storyId: "",
  isCreator: false,
  conference: null,
  hasRecordingTracks: false,
  participant: { participantName: "", participantType: null, id: null, email: null },
  localMediaRecorder: null,
  localRecordingId: null,
  localRecordingInfo: null,
  guestToken: null,
  chat: false,
  callSettings: false,
  recordSettings: false,
  creatorUserName: null,
  messages: [],
  accumulatedRecordingTime: 0,
  accumulatedRecordingTimeBackup: 0,
  visitorId: null,
  streamStorage: null,
  featureFlags: null,
  newMessagesCount: 0,
  creatorVisitorId: null,
  isTimerActive: false,
  recordingTime: {
    minutes: 0,
    seconds: 0,
  },
  visibleParticipants: [],
  callParticipants: [],
  audioOnly: false,
  stopRecordingModalActive: false,
  recordingStopped: false,
  participantsPanel: false,
  visitorIds: [],
  newPath: null,
  uploadingScreenVisible: false,
  localRecordingUploaded: false,
  authorsQuestions: false,
  hasPassword: false,
  prepsession: {
    type: null,
    questions: [],
    termsRequired: false,
    terms: "",
  },
  mediaStream: null,
  showInviteModal: false,
  audioSource: null,
  videoSource: null,
};

export interface DolbyCallActions {
  createConference: Action<this, StartRoomApiResponse>;
  updateConference: Action<this, UpdateConference>;
  cleanConference: Action<this>;
  stopLocalRecording: Action<this>;
  pauseLocalRecording: Action<this>;
  resumeLocalRecording: Action<this>;
  accumulateRecordingTime: Action<this, number>;
  handleChat: Action<this, boolean>;
  handleQuestions: Action<this, boolean>;
  handleCountNewMessage: Action<this>;
  handleCallSettings: Action<this, boolean>;
  handleRecordSettings: Action<this, boolean>;
  handleParticipantsPanel: Action<this, boolean>;
  sendNewMessage: Action<this, ChatMessage>;
  clearChat: Action<this>;
  setName: Action<this, string>;
  setNewConferenceId: Action<this, string>;
  handleTimer: Action<this, boolean>;
  setRecordingTime: Action<this, { minutes: number; seconds: number }>;
  updateLocalMediaRecorder: Action<
    this,
    {
      mediaRecorder: MediaRecorder;
      localRecordingInfo: LocalRecordingInfo;
      localRecordingId: string;
      streamStorage: InMemoryLocalAudioStream | InMemoryLocalMediaStream;
      videoSource: MediaStreamTrack;
      audioSource: MediaStreamTrack;
    }
  >;
  setParticipant: Action<this, { participant: string; show: boolean }>;
  setVisibleParticipants: Action<this, string[]>;
  setCallParticipants: Action<this, string[]>;
  setAudioOnly: Action<this, boolean>;
  setStopRecordingModalActive: Action<this, boolean>;
  setRecordingStopped: Action<this>;
  updateToken: Action<this, string>;
  setVisitorIds: Action<this, string[]>;
  setNewPath: Action<this, string>;
  setUploadingScreenVisible: Action<this, boolean>;
  setLocalRecordingUploaded: Action<this, boolean>;
  setMediaStream: Action<this, MediaStream>;
  handleInviteModal: Action<this, boolean>;
}

export interface DolbyCallThunks {
  createConferenceThunk: Thunk<this, { featureFlagState: FeatureFlagState; visitorId: string }>;
  joinConferenceThunk: Thunk<this, JoinConference>;
  startLocalRecording: Thunk<this, { auth: AuthData; devices: Devices }>;
  updateRecordingTime: Thunk<this, { amount: number }>;
  updateChatThunk: Thunk<this, ChatMessage>;
  updateParticipantsRecordingSettings: Thunk<this, boolean>;
  stopRecording: Thunk<this>;
  refreshToken: Thunk<this>;
}

export interface DolbyCallModel extends DolbyCallState, DolbyCallActions, DolbyCallThunks {}

export const dolbyCallModel: DolbyCallModel = {
  call: newState,

  createConference: action((state, payload) => {
    state.call.storyId = payload.storyId;
    state.call.isCreator = payload.creator;
    state.call.status = CallStatus.CREATED;
  }),

  updateLocalMediaRecorder: action((state, payload) => {
    state.call.localMediaRecorder = payload.mediaRecorder;
    state.call.localRecordingInfo = payload.localRecordingInfo;
    state.call.localRecordingId = payload.localRecordingId;
    state.call.localRecordingId = payload.localRecordingId;
    state.call.streamStorage = payload.streamStorage;
    state.call.audioSource = payload.audioSource;
    state.call.videoSource = payload.videoSource;
  }),

  updateConference: action((state, payload) => {
    const { name, token, email, userId } = JSON.parse(payload.token);
    state.call.token = token;
    state.call.participant.participantName = name;
    state.call.participant.email = email;
    state.call.participant.id = userId;
    state.call.isCreator = payload.creator;
    state.call.creatorUserName = payload.creatorUserName;
    state.call.status = CallStatus.CREATED;
    state.call.storyId = payload.storyId;
    state.call.guestToken = payload.guestToken;
    state.call.accumulatedRecordingTime = payload.accumulatedRecordingTime;
    state.call.visitorId = payload.visitorId;
    state.call.featureFlags = payload.featureFlags;
    state.call.messages = payload.chat;
    state.call.creatorVisitorId = payload.creatorVisitorId;
    state.call.recordingStopped = payload.hasRecordingTracks;
    state.call.hasRecordingTracks = payload.hasRecordingTracks;
    state.call.prepsession.terms = payload.prepsession?.terms;
    state.call.prepsession.termsRequired = payload.prepsession?.termsRequired;
    state.call.prepsession.questions = payload.questions;
    state.call.prepsession.type = payload.prepsession?.type;
    state.call.hasPassword = payload.hasPassword;
  }),

  accumulateRecordingTime: action((state, payload) => {
    state.call.accumulatedRecordingTimeBackup = payload;
  }),

  cleanConference: action((state) => {
    state.call = newState;
  }),

  setStopRecordingModalActive: action((state, payload) => {
    state.call.stopRecordingModalActive = payload;
  }),

  handleChat: action((state, payload) => {
    state.call.callSettings = false;
    state.call.recordSettings = false;
    state.call.participantsPanel = false;
    state.call.authorsQuestions = false;
    state.call.chat = payload;
    if (payload) {
      state.call.newMessagesCount = 0;
    }
  }),

  handleQuestions: action((state, payload) => {
    state.call.callSettings = false;
    state.call.recordSettings = false;
    state.call.participantsPanel = false;
    state.call.chat = false;
    state.call.authorsQuestions = payload;
  }),

  handleCountNewMessage: action((state) => {
    state.call.newMessagesCount += 1;
  }),

  sendNewMessage: action((state, payload) => {
    state.call.messages = [payload, ...state.call.messages];
  }),

  clearChat: action((state) => {
    state.call.messages = [];
  }),

  setVisitorIds: action((state, payload) => {
    state.call.visitorIds = payload;
  }),

  handleTimer: action((state, payload) => {
    state.call.isTimerActive = payload;
  }),

  setUploadingScreenVisible: action((state, payload) => {
    state.call.uploadingScreenVisible = payload;
  }),

  setLocalRecordingUploaded: action((state, payload) => {
    state.call.localRecordingUploaded = payload;
  }),

  setRecordingTime: action((state, payload) => {
    state.call.recordingTime = payload;
  }),

  handleCallSettings: action((state, payload) => {
    state.call.chat = false;
    state.call.authorsQuestions = false;
    state.call.recordSettings = false;
    state.call.participantsPanel = false;
    state.call.callSettings = payload;
  }),

  handleRecordSettings: action((state, payload) => {
    state.call.chat = false;
    state.call.authorsQuestions = false;
    state.call.callSettings = false;
    state.call.participantsPanel = false;
    state.call.recordSettings = payload;
  }),

  handleParticipantsPanel: action((state, payload) => {
    state.call.chat = false;
    state.call.authorsQuestions = false;
    state.call.callSettings = false;
    state.call.recordSettings = false;
    state.call.participantsPanel = payload;
  }),

  setName: action((state, payload) => {
    state.call.participant.participantName = payload;
  }),

  setNewConferenceId: action((state, payload) => {
    state.call.conference = payload;
  }),

  setNewPath: action((state, payload) => {
    state.call.newPath = payload;
  }),

  updateChatThunk: thunk(async (actions, payload, helper) => {
    const { storyId } = helper.getState().call;
    try {
      await axios.post(`/dolby/update_chat/${storyId}`, payload);
      actions.sendNewMessage(payload);
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("StoryId", storyId);
        scope.setExtra("call", helper.getState().call);
        scope.setTag("method", "updateChatThunk");
        Sentry.captureException(err);
      });
    }
  }),

  stopRecording: thunk(async (actions, payload, helper) => {
    const { storyId } = helper.getState().call;
    try {
      await axios.post(`/dolby/stop-recording/${storyId}`);
      actions.setRecordingStopped();
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("StoryId", storyId);
        scope.setExtra("call", helper.getState().call);
        scope.setTag("method", "stopRecording");
        Sentry.captureException(err);
      });
    }
  }),

  refreshToken: thunk(async (actions) => {
    try {
      const res: RefreshToken = await axios.post(`/dolby/refresh_token`);
      actions.updateToken(res.token);
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("method", "refreshToken");
        Sentry.captureException(err);
      });
    }
  }),

  updateToken: action((state, payload) => {
    state.call.token = payload;
  }),

  setRecordingStopped: action((state) => {
    state.call.recordingStopped = true;
  }),

  updateParticipantsRecordingSettings: thunk(async (actions, payload, helper) => {
    const { conference, visibleParticipants } = helper.getState().call;
    try {
      if (conference) {
        await axios.post(`/dolby/update-participants`, {
          conference,
          participants: visibleParticipants,
          finalized: payload,
        });
      }
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setTag("conference", conference);
        scope.setExtra("visibleParticipants", visibleParticipants);
        scope.setExtra("payload", payload);
        scope.setExtra("call data", helper.getState().call);
        scope.setTag("method", "updateParticipantsRecordingSettings");
        Sentry.captureException(e);
      });
      console.log(e);
    }
  }),

  createConferenceThunk: thunk(async (actions, payload) => {
    const startParams = {
      visitor_id: payload.visitorId,
      localRecordingStreaming: payload.featureFlagState.flags?.streamingUpload,
      localRecordingMimeType: payload.featureFlagState.flags?.localRecordingMimeType,
      localRecordingVideoBitrate: payload.featureFlagState.flags?.localVideoRecordingBitrate,
      localRecordingMaxResolution: payload.featureFlagState.flags?.localVideoMaxResolution,
      disableAutomaticResolutionSwitching: payload.featureFlagState.flags?.disableAutomaticResolutionSwitching,
    };
    try {
      const response = await axios.post(`/stories/start`, {
        ...startParams,
        videoCallProvider: VideoCallProvider.DOLBY,
      });
      const res: StartRoomApiResponse = response.data;
      watchLocalRecordingCallState();
      reportLocalRecordingCallState();
      actions.createConference(res);
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setExtra("startParams", startParams);
        scope.setTag("method", "createConferenceThunk");
        Sentry.captureException(e);
      });
      console.error(e);
    }
  }),

  setVisibleParticipants: action((state, payload) => {
    state.call.visibleParticipants = payload;
  }),

  setParticipant: action((state, payload) => {
    if (payload.show) {
      if (!state.call.visibleParticipants.some((participant) => participant === payload.participant)) {
        state.call.visibleParticipants = [payload.participant, ...state.call.visibleParticipants];
      }
    } else {
      state.call.visibleParticipants = state.call.visibleParticipants.filter(
        (participant) => participant !== payload.participant
      );
    }
  }),

  setAudioOnly: action((state, payload) => {
    state.call.audioOnly = payload;
  }),

  setMediaStream: action((state, payload) => {
    state.call.mediaStream = payload;
  }),

  handleInviteModal: action((state, payload) => {
    state.call.showInviteModal = payload;
  }),

  setCallParticipants: action((state, payload) => {
    state.call.callParticipants = payload;
  }),

  joinConferenceThunk: thunk(async (actions, payload) => {
    const response = await axios.post(`/stories/${payload.interviewId}/join`, {
      visitorId: payload.visitorId ? payload.visitorId : Date.now(),
      videoCallProvider: VideoCallProvider.DOLBY,
    });
    const res: JoinRoomApiResponse = response.data;
    //
    actions.updateConference({ ...res, visitorId: payload.visitorId });
  }),

  stopLocalRecording: action((state) => {
    if (!state.call.localMediaRecorder) return;
    state.call.localMediaRecorder.stop();
    state.call.localMediaRecorder.stream.getTracks().forEach((track) => track.stop());
    state.call.localMediaRecorder = null;
    state.call.localRecordingInfo = null;
  }),

  pauseLocalRecording: action((state) => {
    if (!state.call.localMediaRecorder) return;
    state.call.localMediaRecorder.pause();
  }),

  resumeLocalRecording: action((state) => {
    if (!state.call.localMediaRecorder) return;
    state.call.localMediaRecorder.resume();
  }),

  updateRecordingTime: thunk(async (actions, payload, helper) => {
    const call = helper.getState().call;
    if (call.isCreator && payload.amount !== 0) {
      await axios.post(`/dolby/recording_duration/${call.storyId}`, {
        accumulatedRecordingTime: payload.amount,
      });
    }
  }),

  startLocalRecording: thunk(async (actions, payload, helper) => {
    const call = helper.getState().call;
    call.hasRecordingTracks = true;
    if (call.isCreator) {
      await axios.post(`/stories/${call.storyId}/start_recording`, {
        localRecording: true,
        visitorId: payload.auth.visitorId,
        videoCallProvider: VideoCallProvider.DOLBY,
      });
    } else {
      setTimeout(() => {
        console.log("Retrasado por 1 segundo.");
      }, 3000);
    }

    try {
      const mediaRecorder = await initMediaRecorder(payload.devices);

      mediaRecorder.stream.getAudioTracks().forEach((track) => {
        track.enabled = true;
      });

      mediaRecorder.stream.getVideoTracks().forEach((track) => {
        track.enabled = true;
      });

      const isStreaming = true;
      const localMediaStorage = getLocalMediaRecordingInstance(isStreaming);

      const videoSettings = mediaRecorder.stream.getVideoTracks()?.[0]?.getSettings();
      const audioSettings = mediaRecorder.stream.getAudioTracks()?.[0]?.getSettings();

      const streamStorage = await localMediaStorage.createStream({
        streamId: mediaRecorder.stream.id,
        storyId: call.storyId,
        sessionId: call.conference,
        localTimeOffset: Date.now(),
        userId: payload.auth.userId,
        type: mediaRecorder.mimeType,
      });

      mediaRecorder.addEventListener("dataavailable", (event) => {
        streamStorage.pushChunk(event).catch((err) => {
          Sentry.withScope((scope) => {
            scope.setExtra("event", event);
            scope.setTag("method", "addEventListener.dataavailable");
            Sentry.captureException(err);
          });
          console.log(err);
        });
      });

      mediaRecorder.addEventListener("start", (event: any) => {
        console.log(event);
      });

      mediaRecorder.addEventListener("pause", (event: any) => {
        console.log(event);
      });

      mediaRecorder.addEventListener("resume", (event: any) => {
        console.log(event);
      });

      mediaRecorder.addEventListener("stop", () => {
        if (isStreaming) {
          streamStorage.finish();
        } else {
          streamStorage
            .finish(true)
            .then(() => streamStorage.delete())
            .catch((err) => {
              console.log(err);
            });
        }
      });

      mediaRecorder.addEventListener("error", (event) => {
        console.log(event);
      });
      mediaRecorder.addEventListener("onerror", (event) => {
        console.log(event);
      });

      mediaRecorder.start(5000);
      const localRecordingId = streamStorage.id;
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevice = devices.find((dev) => dev.deviceId === videoSettings?.deviceId);
      const audioDevice = devices.find((dev) => dev.deviceId === audioSettings?.deviceId);
      const videoSource = mediaRecorder.stream.getVideoTracks()[0];
      const audioSource = mediaRecorder.stream.getAudioTracks()[0];
      const localRecordingInfo = {
        recordingId: streamStorage.id,
        videoSettings,
        audioSettings,
        videoLabel: videoDevice?.label,
        audioLabel: audioDevice?.label,
        videoBitrate: mediaRecorder.videoBitsPerSecond,
        audioBitrate: mediaRecorder.audioBitsPerSecond,
        videoSource: videoSource.id,
        audioSource: audioSource.id,
      };
      actions.updateLocalMediaRecorder({
        mediaRecorder,
        localRecordingInfo,
        localRecordingId,
        streamStorage,
        videoSource,
        audioSource,
      });
    } catch (err) {
      console.error(err);
    }
  }),
};

async function initMediaRecorder(payload: Devices) {
  const localRecordingMimeType = "video/webm;codecs=vp8,opus";
  const localRecordingVideo = localRecordingMimeType?.startsWith("video");
  const localRecordingMaxResolution = { width: 1920, height: 1080 };

  const { stream: mediaStream } = await getChromeSources(
    localRecordingVideo,
    localRecordingMaxResolution,
    payload.camera.getSelectedCamera().deviceId,
    payload.mic.getSelectedMicrophone().deviceId
  );

  const mimeType = localRecordingMimeType || "audio/webm;codecs=opus";
  const recorderOpts = MediaRecorder.isTypeSupported(mimeType)
    ? {
        audioBitsPerSecond: 48000,
        videoBitsPerSecond: 4000000,
        mimeType: mimeType,
      }
    : {}; // no opus support, use defaults (necessary with polyfill)

  return new MediaRecorder(mediaStream, recorderOpts);
}

async function getChromeSources(
  videoEnabled = true,
  maxResolution = { width: 1920, height: 1080 },
  videoSource,
  audioSource,
  maxRetries = 3
) {
  let retries = 0;
  let stream;

  while (retries < maxRetries) {
    try {
      stream = await window.navigator.mediaDevices.getUserMedia({
        video: videoEnabled
          ? {
              deviceId: { exact: videoSource },
              width: { ideal: maxResolution.width },
              height: { ideal: maxResolution.height },
            }
          : false,
        audio: getAudioSourceConstraints(audioSource),
      });

      return {
        stream,
      };
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setExtra("maxResolution", maxResolution);
        scope.setExtra("videoEnabled", videoEnabled);
        scope.setExtra("audioSource", audioSource);
        scope.setExtra("videoSource", videoSource);
        scope.setTag("method", "getChromeSources");
        Sentry.captureException(error);
      });
      console.error("Error, Could not start video source:", error);
      retries += 1;
    }
  }

  console.error(`Error, Could not start video source: max retries ${maxRetries}`);
  return {
    stream: null,
  };
}

function getAudioSourceConstraints(audioSource: string) {
  const opts = getCallPublisherOpts();
  const constraints: MediaTrackConstraints = { deviceId: { exact: audioSource } };

  if (opts.enableStereo) {
    constraints.channelCount = 2;
  }

  ["echoCancellation", "autoGainControl", "noiseSuppression"].forEach((propName) => {
    constraints[propName] = !opts.disableAudioProcessing && opts[propName];
  });

  return constraints;
}

function getCallPublisherOpts() {
  return {
    autoGainControl: true,
    disableAudioProcessing: false,
    echoCancellation: true,
    enableStereo: false,
    noiseSuppression: true,
  };
}
